import React, { useState, Children, useRef, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useThemeToken } from '@/lib/theme';

export default function Stepper({
  children,
  initialStep = 1,
  onStepChange = () => {},
  onFinalStepCompleted = () => {},
  stepCircleContainerClassName = '',
  stepContainerClassName = '',
  contentClassName = '',
  footerClassName = '',
  backButtonProps = {},
  nextButtonProps = {},
  backButtonText = 'Back',
  nextButtonText = 'Continue',
  // DEVIATION FROM UPSTREAM: what the last step's button says, and whether
  // pressing it ends the stepper or wraps it.
  //
  // Upstream's final button is "Complete", and completing sets currentStep past
  // the end, which animates the content to height 0 AND unmounts the footer
  // holding both buttons. That is right for a signup wizard, where finishing
  // means leaving. Here the stepper is how a phone reads "How it works" on the
  // landing page, and there is nothing to complete — pressing it left a titled
  // section containing an empty card and no way back, which is the worst state
  // on the page and the easiest one to reach.
  finalButtonText,
  wrapOnComplete = false,
  disableStepIndicators = false,
  renderStepIndicator,
  ...rest
}) {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [direction, setDirection] = useState(0);
  const stepsArray = Children.toArray(children);
  const totalSteps = stepsArray.length;
  const isCompleted = currentStep > totalSteps;
  const isLastStep = currentStep === totalSteps;

  const updateStep = newStep => {
    setCurrentStep(newStep);
    if (newStep > totalSteps) onFinalStepCompleted();
    else onStepChange(newStep);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setDirection(-1);
      updateStep(currentStep - 1);
    }
  };

  const handleNext = () => {
    if (!isLastStep) {
      setDirection(1);
      updateStep(currentStep + 1);
    }
  };

  const handleComplete = () => {
    if (wrapOnComplete) {
      // Back to the first step rather than past the last. Travelling backwards
      // so the slide reads as rewinding, which is what it is.
      setDirection(-1);
      updateStep(1);
      onFinalStepCompleted();
      return;
    }
    setDirection(1);
    updateStep(totalSteps + 1);
  };

  return (
    <div
      // DEVIATION FROM UPSTREAM: the padding is phone-first. Upstream nests
      // p-4 around p-8 around another px-8, which is 128px of horizontal
      // padding on every side of the prose — on a 375px screen that left the
      // step text about 175px wide, roughly four words a line. Each of the
      // three drops a step below sm and hands it straight back at sm, so the
      // desktop card is the card it always was.
      className="flex min-h-full flex-1 flex-col items-center justify-center p-0 sm:aspect-[4/3] sm:p-4 md:aspect-[2/1]"
      {...rest}
    >
      <div
        // DEVIATION FROM UPSTREAM: no hardcoded #222 border. It was an inline
        // style, so it beat the border utility the caller passes in
        // stepCircleContainerClassName and there was no way to override it.
        className={`mx-auto w-full max-w-md rounded-4xl shadow-xl ${stepCircleContainerClassName}`}
      >
        <div className={`${stepContainerClassName} flex w-full items-center p-5 sm:p-8`}>
          {stepsArray.map((_, index) => {
            const stepNumber = index + 1;
            const isNotLastStep = index < totalSteps - 1;
            return (
              <React.Fragment key={stepNumber}>
                {renderStepIndicator ? (
                  renderStepIndicator({
                    step: stepNumber,
                    currentStep,
                    onStepClick: clicked => {
                      setDirection(clicked > currentStep ? 1 : -1);
                      updateStep(clicked);
                    }
                  })
                ) : (
                  <StepIndicator
                    step={stepNumber}
                    disableStepIndicators={disableStepIndicators}
                    currentStep={currentStep}
                    onClickStep={clicked => {
                      setDirection(clicked > currentStep ? 1 : -1);
                      updateStep(clicked);
                    }}
                  />
                )}
                {isNotLastStep && <StepConnector isComplete={currentStep > stepNumber} />}
              </React.Fragment>
            );
          })}
        </div>
        <StepContentWrapper
          isCompleted={isCompleted}
          currentStep={currentStep}
          direction={direction}
          className={`space-y-2 px-4 sm:px-8 ${contentClassName}`}
        >
          {stepsArray[currentStep - 1]}
        </StepContentWrapper>
        {!isCompleted && (
          <div className={`px-4 pb-6 sm:px-8 sm:pb-8 ${footerClassName}`}>
            <div className={`mt-10 flex ${currentStep !== 1 ? 'justify-between' : 'justify-end'}`}>
              {currentStep !== 1 && (
                <button
                  onClick={handleBack}
                  className={`duration-350 inline-flex min-h-11 items-center rounded px-2 transition sm:min-h-0 sm:py-1 ${
                    currentStep === 1
                      ? 'pointer-events-none opacity-50 text-faint'
                      : 'text-faint hover:text-ink'
                  }`}
                  {...backButtonProps}
                >
                  {backButtonText}
                </button>
              )}
              <button
                onClick={isLastStep ? handleComplete : handleNext}
                className="duration-350 flex min-h-11 items-center justify-center rounded-full bg-ink px-5 font-medium tracking-tight text-paper transition hover:bg-ink/85 sm:min-h-0 sm:py-1.5 sm:px-3.5"
                {...nextButtonProps}
              >
                {isLastStep ? (finalButtonText ?? 'Complete') : nextButtonText}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StepContentWrapper({ isCompleted, currentStep, direction, children, className }) {
  const [parentHeight, setParentHeight] = useState(0);

  return (
    <motion.div
      style={{ position: 'relative', overflow: 'hidden' }}
      animate={{ height: isCompleted ? 0 : parentHeight }}
      transition={{ type: 'spring', duration: 0.4 }}
      className={className}
    >
      <AnimatePresence initial={false} mode="sync" custom={direction}>
        {!isCompleted && (
          <SlideTransition key={currentStep} direction={direction} onHeightReady={h => setParentHeight(h)}>
            {children}
          </SlideTransition>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function SlideTransition({ children, direction, onHeightReady }) {
  const containerRef = useRef(null);

  // DEVIATION FROM UPSTREAM: measured on resize, not only when the children
  // change. Upstream reads offsetHeight once per step, which assumes the
  // stepper is visible and a fixed width for its whole life. This one is
  // `md:hidden`, so at desktop widths it measures 0 while display:none — and
  // dragging a window back under 768px left a correctly-rendered step inside a
  // wrapper still animated to height: 0. A ResizeObserver fires when it becomes
  // visible again, and on every reflow in between.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    onHeightReady(el.offsetHeight);
    const ro = new ResizeObserver(() => onHeightReady(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [children, onHeightReady]);

  return (
    <motion.div
      ref={containerRef}
      custom={direction}
      variants={stepVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.4 }}
      style={{ position: 'absolute', left: 0, right: 0, top: 0 }}
    >
      {children}
    </motion.div>
  );
}

const stepVariants = {
  enter: dir => ({
    x: dir >= 0 ? '-100%' : '100%',
    opacity: 0
  }),
  center: {
    x: '0%',
    opacity: 1
  },
  exit: dir => ({
    x: dir >= 0 ? '50%' : '-50%',
    opacity: 0
  })
};

export function Step({ children }) {
  return <div className="px-4 sm:px-8">{children}</div>;
}

function StepIndicator({ step, currentStep, onClickStep, disableStepIndicators }) {
  const status = currentStep === step ? 'active' : currentStep < step ? 'inactive' : 'complete';
  // DEVIATION FROM UPSTREAM: the indicator is ink and paper rather than a fixed
  // near-black and violet. Those are colours from react-bits' own dark palette
  // and belong to no theme here — the inactive #222 all but vanishes on a dark
  // card. Resolved rather than passed as var(): framer-motion interpolates
  // between two colours, and it cannot interpolate an unresolved variable.
  const ink = useThemeToken('--c-ink');
  // No `paper` token here on purpose: the glyph inside the indicator is a child
  // carrying `text-paper` / `bg-paper`, so the paper half of this comes from the
  // stylesheet. Resolving it again in JavaScript bound a value nothing read.
  const subtle = useThemeToken('--c-subtle', '#F8FAFC');
  const faint = useThemeToken('--c-faint', '#94A3B8');

  const handleClick = () => {
    if (step !== currentStep && !disableStepIndicators) onClickStep(step);
  };

  return (
    <motion.div
      onClick={handleClick}
      // The circle is 32px, which is a small thing to hit with a thumb. The
      // padding is cancelled by an equal negative margin, so the hit area grows
      // to 44px while the indicator occupies exactly the box it did before and
      // the connectors either side stay where they were.
      className={`relative -m-1.5 p-1.5 outline-none focus:outline-none sm:m-0 sm:p-0 ${disableStepIndicators ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}
      animate={status}
      initial={false}
    >
      <motion.div
        variants={{
          inactive: { scale: 1, backgroundColor: subtle, color: faint },
          active: { scale: 1, backgroundColor: ink, color: ink },
          complete: { scale: 1, backgroundColor: ink, color: ink }
        }}
        transition={{ duration: 0.3 }}
        className="flex h-8 w-8 items-center justify-center rounded-full font-semibold"
      >
        {status === 'complete' ? (
          <CheckIcon className="h-4 w-4 text-paper" />
        ) : status === 'active' ? (
          <div className="h-3 w-3 rounded-full bg-paper" />
        ) : (
          <span className="text-sm">{step}</span>
        )}
      </motion.div>
    </motion.div>
  );
}

function StepConnector({ isComplete }) {
  const ink = useThemeToken('--c-ink');
  const lineVariants = {
    incomplete: { width: 0, backgroundColor: 'transparent' },
    complete: { width: '100%', backgroundColor: ink }
  };

  return (
    <div className="relative mx-2 h-0.5 flex-1 overflow-hidden rounded bg-line/20">
      <motion.div
        className="absolute left-0 top-0 h-full"
        variants={lineVariants}
        initial={false}
        animate={isComplete ? 'complete' : 'incomplete'}
        transition={{ duration: 0.4 }}
      />
    </div>
  );
}

function CheckIcon(props) {
  return (
    <svg {...props} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <motion.path
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ delay: 0.1, type: 'tween', ease: 'easeOut', duration: 0.3 }}
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}
